import {
  faFileExport,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';

import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useState, useEffect } from 'react';
import { Card, Col, Offcanvas, Row, Toast, ToastContainer } from 'react-bootstrap';
import apiClient, { handleAPIError } from 'helpers/apiService';
import { useAuth } from 'providers/AuthProvider';
import { CustomerData } from './components/DataDefinitions';
import { ColumnDef } from '@tanstack/react-table';
import { Link, Navigate } from 'react-router-dom';
import RevealDropdown, { RevealDropdownTrigger } from 'components/base/RevealDropdown';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import { useNavigate } from "react-router-dom";
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import SignUpForm from 'pages/authentication/components/SignUpForm';


const ActionDropdownItems = ({ contact_id, onDeleteContact }: {
  contact_id: string | number,
  onDeleteContact: (contact_id: string | number) => void;
}) => {
  return (
    <>
      <Dropdown.Item eventKey="1" onClick={() => onDeleteContact(contact_id)} className="text-danger">
        Remove
      </Dropdown.Item>
    </>
  );
};

const CustomersTable = () => {
  return (
    <div className="border-top">
      <AdvanceTable
        tableProps={{ className: 'phoenix-table fs-9' }}
        rowClassName="hover-actions-trigger btn-reveal-trigger"
      />
      <AdvanceTableFooter pagination className="py-4" />
    </div>
  );
};


const CustomerSelector = () => {
  const auth = useAuth();
  const [customers, setCustomers] = useState<CustomerData[]>([]);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  // State to control Toast messages
  const [show, setShow] = useState(false);

  const [startCustomerSetup, setStartCustomerSetup] = useState(false);
  const handleClose = () => setStartCustomerSetup(false);

  const [toastMessage, setToastMessage] = useState('');

  const breadcrumbItems: PageBreadcrumbItem[] = [
    {
      label: t('Dashboard'),
      url: '/dashboard/home'
    },
    {
      label: t('Customers.page_title'),
      active: true
    }
  ];

  const customersTableColumns: ColumnDef<CustomerData>[] = [
    {
      accessorKey: 'customer_id',
      header: '#',
      cell: ({ row: { original } }) => {
        return (
          <Link to={`#`} onClick={() => setCustomerSession(original.customer_id)} className="fs-8 fw-bold">
            {original.customer_id}
          </Link>
        );
      },
      meta: {
        headerProps: { style: { width: '5%' }, className: 'ps-0' },
        cellProps: { className: 'white-space-nowrap ps-0' }
      }
    },

    {
      accessorKey: 'customer_name',
      header: 'Name',
      cell: ({ row: { original } }) => {

        return (
          <Link to={`#`} onClick={() => setCustomerSession(original.customer_id)} className="fs-8 fw-bold">
            {original.customer_name}
          </Link>
        );
      },
      meta: {
        headerProps: { style: { width: '25%' }, className: 'ps-0' },
        cellProps: { className: 'white-space-nowrap ps-0' }
      }
    },

    {
      accessorKey: 'inserted_at',
      header: 'Created date',
      cell: ({ row: { original } }) => original.inserted_at,
      meta: {
        headerProps: {
          style: { width: '15%' },
          className: 'ps-4 pe-5'
        },
        cellProps: {
          className: 'white-space-nowrap ps-4 text-700'
        }
      }
    },
    {
      id: 'dropdown',
      cell: ({ row: { original } }) => {
        return (
          <RevealDropdownTrigger>
            <RevealDropdown>
              <ActionDropdownItems contact_id={original.customer_id} onDeleteContact={deleteCustomer} />
            </RevealDropdown>
          </RevealDropdownTrigger>
        );
      },
      meta: {
        cellProps: {
          className: 'text-end pe-0 ps-4'
        }
      }
    }
  ];

  const setCustomerSession = async (customer_id: number) => {
    try {
      const { data } = await apiClient.post(`/v1/admin/customers/${customer_id}`);

      if (auth.user == null)
        return

      auth.setAccessToken(data.access_token);
      auth.setUser({
        ...auth.user,
        customer_id: customer_id,
        customer_name: data.customer_name
      });

      navigate("/dashboard/home");


    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }

  const fetchData = async () => {
    try {
      const response = await apiClient.get(
        '/v1/admin/customers',
        {
          headers: {
            'accept': 'application/json',

          }
        }
      );
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);


  const table = useAdvanceTable({
    data: customers,
    columns: customersTableColumns,
    pageSize: 5,
    pagination: true,
    sortable: true,
    selection: true,
    initialState: {
      columnVisibility: {
        status: false,
        designation: false
      }
    }
  });

  const handleClick = () => {
    setStartCustomerSetup(true);
  };

  const deleteCustomer = async (customer_id: string | number) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      const response = await apiClient.delete(
        `/v1/admin/customers/${customer_id}`,
        {
          headers: {
            'accept': 'application/json',

          }
        }
      );
      setToastMessage(response.data['message'])
      setShow(true);
      fetchData();
    }
  };

  if (auth.user == undefined || auth.user?.customer_id > 0) {
    navigate("/dashboard/home")
  }

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <PageBreadcrumb items={breadcrumbItems} />

      <Offcanvas show={startCustomerSetup} onHide={handleClose} backdrop="static" placement="end" style={{ 'width': '35%' }}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className='fs-5' >New Customer Setup</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>

          <SignUpForm layout="simple" isAdmin={true} />

        </Offcanvas.Body>
      </Offcanvas>


      <ToastContainer className="p-3" position='top-center'>
        <Toast onClose={() => setShow(false)} show={show} delay={3000} autohide>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      <Card className='mt-3  mb-3 border border-light rounded-5' >
        <Card.Body>
          <AdvanceTableProvider {...table}>
            <Row className="g-3 justify-content-between mb-4">
              <Col xs="auto">
                <Button
                  variant="primary"
                  className="me-4"
                  startIcon={
                    <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                  }
                  onClick={handleClick}
                >
                  {t('Customers.add_customer')}
                </Button>

                <Button
                  variant="link"
                  className="text-900 px-0"
                  startIcon={
                    <FontAwesomeIcon icon={faFileExport} className="fs-9 me-2" />
                  }
                >
                  {t("Customer.export")}
                </Button>
              </Col>
            </Row>
            <CustomersTable />
          </AdvanceTableProvider>
        </Card.Body>
      </Card>
    </>
  );
};

export default CustomerSelector;